import { getFocusableElements } from "@theme/a11y";
import { events } from "@theme/event-bus";
import {
  breakpoints,
  classes,
  getAttributeValue,
  isFromBreakpoint,
  isUntilBreakpoint,
} from "@theme/helpers";
import {
  HTMLCustomElement,
  HTMLShopifySectionElement,
} from "@theme/html-custom-element";

/**
 * When there is no hero section in the main content, the header should not use
 * transparent / alternate styling. The hero section is not a sibling of the
 * header, so the check is deferred until the full document (including main) exists.
 */
function syncNoHeroBannerClass() {
  const hasHeroInMain = Boolean(
    document.querySelector("main .hero-banner, #MainContent .hero-banner"),
  );

  document.documentElement.classList.toggle("no-hero-banner", !hasHeroInMain);
}

function keepsMobileLogoInFlow() {
  return !document.body.classList.contains("template-index");
}

class SiteHeader extends HTMLCustomElement {
  static chromeAwayClass = "header-chrome-away";
  static chromeHideAfter = 200;
  static chromeShowBefore = 80;
  static desktopAwayClass = "header-desktop-away";
  static desktopScrollDelta = 8;
  static desktopHideAfter = 80;

  #chromeMql = null;
  #lastScrollY = 0;
  #onChromeBreakpointChange = () => this.#syncChrome();

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", syncNoHeroBannerClass, {
        once: true,
      });
    } else {
      queueMicrotask(syncNoHeroBannerClass);
    }

    this.#lastScrollY = window.scrollY;
    this.on(
      "scroll",
      window,
      () => {
        document.documentElement.classList.toggle(
          "header-scrolled",
          window.scrollY > 0,
        );
        this.#syncChrome();
      },
      { passive: true },
      true,
    );

    if (!this.#chromeMql) {
      this.#chromeMql = window.matchMedia(
        `(max-width: ${breakpoints.large - 1}px)`,
      );
    }
    this.#chromeMql.addEventListener("change", this.#onChromeBreakpointChange);
    this.#syncChrome();
  }

  disconnectedCallback() {
    this.#chromeMql?.removeEventListener(
      "change",
      this.#onChromeBreakpointChange,
    );
    this.#setDesktopHeaderAway(false);
    document.documentElement.classList.remove(
      "header-scrolled",
      SiteHeader.chromeAwayClass,
      SiteHeader.desktopAwayClass,
    );
    super.disconnectedCallback();
  }

  #syncChrome() {
    if (isFromBreakpoint("large")) {
      document.documentElement.classList.remove(SiteHeader.chromeAwayClass);
      this.#syncDesktopChrome();
      return;
    }

    this.#setDesktopHeaderAway(false);
    this.#syncMobileChrome();
  }

  /**
   * On desktop, slide the header and announcement bar away while scrolling
   * down, and bring them back as soon as the user scrolls up.
   */
  #syncDesktopChrome() {
    const y = window.scrollY;
    const delta = y - this.#lastScrollY;
    this.#lastScrollY = y;

    const header = this.closest("header") ?? this;
    const announcement = document.querySelector(".announcement-bar");
    const menuOpen = Boolean(
      this.querySelector(".header-menu__submenu.is-open"),
    );

    if (
      y <= 0 ||
      menuOpen ||
      header.matches(":hover, :focus-within") ||
      announcement?.matches(":hover, :focus-within")
    ) {
      this.#setDesktopHeaderAway(false);
      return;
    }

    if (
      delta > SiteHeader.desktopScrollDelta &&
      y > SiteHeader.desktopHideAfter
    ) {
      this.#setDesktopHeaderAway(true);
    } else if (delta < -SiteHeader.desktopScrollDelta) {
      this.#setDesktopHeaderAway(false);
    }
  }

  /**
   * @param {boolean} away
   */
  #setDesktopHeaderAway(away) {
    document.documentElement.classList.toggle(
      SiteHeader.desktopAwayClass,
      away,
    );
    this.closest("header")?.toggleAttribute("inert", away);
    document.querySelector(".announcement-bar")?.toggleAttribute("inert", away);
  }

  /**
   * On mobile, keep the announcement bar and relocated logo pinned for a
   * stretch of scroll, then slide both away. They return near the top.
   * Every template except the homepage skips this: the logo stays in document flow.
   */
  #syncMobileChrome() {
    const root = document.documentElement;

    if (!isUntilBreakpoint("large") || keepsMobileLogoInFlow()) {
      root.classList.remove(SiteHeader.chromeAwayClass);
      return;
    }

    const y = window.scrollY;
    const isAway = root.classList.contains(SiteHeader.chromeAwayClass);

    if (isAway) {
      if (y < SiteHeader.chromeShowBefore) {
        root.classList.remove(SiteHeader.chromeAwayClass);
      }
    } else if (y > SiteHeader.chromeHideAfter) {
      root.classList.add(SiteHeader.chromeAwayClass);
    }
  }
}

/**
 * Moves the mobile toolbar out of the `<header>` section wrapper on connect so
 * the fixed bottom-of-viewport toolbar is not nested inside the page header
 * (and is not affected by anything scoped to that wrapper). Cleans up any
 * orphaned toolbar left behind by a previous section instance (e.g. after a
 * theme editor section reload).
 */
class HeaderToolbar extends HTMLCustomElement {
  static movedAttribute = "data-moved";

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (this.hasAttribute(HeaderToolbar.movedAttribute)) return;

    const header = this.closest("header");
    if (!header || !header.parentNode) return;

    for (const existing of document.querySelectorAll(
      `header-toolbar[${HeaderToolbar.movedAttribute}]`,
    )) {
      if (existing !== this) existing.remove();
    }

    this.setAttribute(HeaderToolbar.movedAttribute, "");
    header.parentNode.insertBefore(this, header.nextSibling);
  }
}

/**
 * On mobile, relocates the header logo out of the `<header>` (`.site-header`)
 * wrapper and into `<body>` so its `mix-blend-mode` can blend against the hero
 * painted behind the header. `.site-header` is a stacking context (it needs a
 * `z-index` for layering and `position: fixed` on desktop), which traps a
 * descendant's blend mode; escaping the header is the only robust way to blend
 * the logo with the content behind it.
 *
 * An inert clone is left in the logo's original slot as a spacer so the header
 * layout/height is preserved. On desktop the logo is restored to its original
 * position (desktop uses `--header-logo-blend-mode: normal`, so no blend and no
 * relocation is needed). The contextual header CSS variables are copied onto the
 * relocated wrapper since it no longer inherits them from `.site-header`.
 */
class SiteHeaderLogo extends HTMLCustomElement {
  static movedAttribute = "data-logo-moved";
  static spacerAttribute = "data-logo-spacer";
  static inheritedVars = ["--header-logo-blend-mode", "--header-text-color"];

  #header = null;
  #spacer = null;
  #mql = null;
  #onBreakpointChange = () => this.#syncPlacement();

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    // Spacer clones must never relocate themselves.
    if (this.hasAttribute(SiteHeaderLogo.spacerAttribute)) return;

    // Relocating ourselves within the DOM fires disconnect/connect via custom
    // element reactions. In that case do not re-initialise (it would drop our
    // header reference now that we live outside the header) — just re-attach the
    // listeners that disconnectedCallback removed.
    if (this.hasAttribute(SiteHeaderLogo.movedAttribute)) {
      this.#ensureBreakpointListener();
      return;
    }

    // Remove any logo left relocated in <body> by a previous section instance
    // (e.g. after a theme editor reload), which would otherwise be orphaned.
    for (const orphan of document.querySelectorAll(
      `site-header-logo[${SiteHeaderLogo.movedAttribute}]`,
    )) {
      if (orphan !== this) orphan.remove();
    }

    this.#header = this.closest("header");
    this.#ensureBreakpointListener();
    this.#syncPlacement();
  }

  disconnectedCallback() {
    this.#mql?.removeEventListener("change", this.#onBreakpointChange);
    if (super.disconnectedCallback) super.disconnectedCallback();
  }

  #ensureBreakpointListener() {
    if (!this.#mql) {
      this.#mql = window.matchMedia(`(max-width: ${breakpoints.large - 1}px)`);
    }
    // addEventListener with the same handler reference is idempotent.
    this.#mql.addEventListener("change", this.#onBreakpointChange);
  }

  #syncPlacement() {
    // All templates except the homepage keep the logo in document flow on
    // mobile so it scrolls away with the header instead of sticking.
    if (isUntilBreakpoint("large") && !keepsMobileLogoInFlow()) {
      this.#moveOut();
    } else {
      this.#restore();
    }
  }

  #moveOut() {
    if (this.hasAttribute(SiteHeaderLogo.movedAttribute)) {
      this.#applyInheritedVars();
      return;
    }

    const header = this.#header;
    if (!header || !header.parentNode) return;

    // Leave an inert clone behind to hold the header logo row's dimensions.
    const liveLink = this.querySelector(".site-header__logo-link");
    const homepageHref =
      liveLink?.getAttribute("href") || window.theme?.routes?.root_url || "/";

    const spacer = this.cloneNode(true);
    spacer.setAttribute(SiteHeaderLogo.spacerAttribute, "");
    spacer.setAttribute("aria-hidden", "true");
    const spacerLink = spacer.querySelector("a");
    // Only disable the spacer copy. `cloneNode` can return a node that still
    // points at the live `<a>`, and stripping href there would break the logo.
    if (spacerLink && spacerLink !== liveLink) {
      spacerLink.setAttribute("tabindex", "-1");
    }
    this.#spacer = spacer;
    this.before(spacer);

    this.setAttribute(SiteHeaderLogo.movedAttribute, "");
    this.#applyInheritedVars();
    header.parentNode.insertBefore(this, header.nextSibling);

    const movedLink = this.querySelector(".site-header__logo-link");
    if (movedLink) movedLink.setAttribute("href", homepageHref);
  }

  #restore() {
    if (!this.hasAttribute(SiteHeaderLogo.movedAttribute)) return;

    this.removeAttribute(SiteHeaderLogo.movedAttribute);
    for (const name of SiteHeaderLogo.inheritedVars) {
      this.style.removeProperty(name);
    }

    if (this.#spacer && this.#spacer.parentNode) {
      this.#spacer.replaceWith(this);
    } else if (this.#header && this.#header.isConnected) {
      // Fallback: re-attach to the header if the spacer was lost.
      this.#header.querySelector(".site-header__container")?.appendChild(this);
    }
    this.#spacer = null;
  }

  /**
   * Copies the header's contextual CSS variables onto the relocated wrapper,
   * which no longer inherits them from `.site-header`. On mobile these values
   * are static per page, so copying on move (and on breakpoint change) is
   * sufficient.
   */
  #applyInheritedVars() {
    if (!this.#header) return;
    const styles = window.getComputedStyle(this.#header);
    for (const name of SiteHeaderLogo.inheritedVars) {
      const value = styles.getPropertyValue(name).trim();
      if (value) this.style.setProperty(name, value);
    }
  }
}

class HeaderMenu extends HTMLShopifySectionElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on("mouseover", this, this.#onMouseOver);
    this.on("mouseleave", this, this.#onMouseLeave);
    this.on("click", this, this.#onClick);
    this.on("focusin", this, this.#onIntent);
    this.on("touchstart", this, this.#onIntent, { passive: true });

    // Reset any drilled-in panel whenever the mobile menu dialog closes.
    this.subscribe(events.toggleElementClosed, ({ id }) => {
      if (id === "header-menu") this.#closeAllSubmenus();
    });

    // Reserve each submenu grid's height from its default content on load so
    // the layout is stable before the menu is ever opened. Deferred a frame so
    // the hidden (but laid-out) mega menu can be measured.
    requestAnimationFrame(() => this.#initSubmenuHeights());
    this.#queueIdleImageLoad();
  }

  /**
   * The mega menu holds hundreds of product images. They ship without a src
   * (see the `defer` option of the image snippet) so they don't compete with
   * the rest of the page, and load here instead: on intent (hover, focus,
   * touch), otherwise one submenu at a time once the page is idle.
   * @param {Event} event
   */
  #onIntent = (event) => {
    const item =
      event.target instanceof Element
        ? event.target.closest(".header-menu__item")
        : null;

    if (item && this.contains(item)) this.#loadMenuImages(item, "high");
  };

  /**
   * @param {Element} root
   * @param {"high" | "low"} priority
   * @returns {Promise<void>} Resolves when the images have loaded, or after a timeout.
   */
  #loadMenuImages(root, priority) {
    const images = [...root.querySelectorAll("img[data-src]")];
    if (!images.length) return Promise.resolve();

    const loaded = images.map(
      (img) =>
        new Promise((resolve) => {
          const { src, srcset } = img.dataset;

          img.setAttribute("data-loading", "");
          img.addEventListener(
            "load",
            () => {
              img.removeAttribute("data-loading");
              img.setAttribute("data-loaded", "");
              resolve();
            },
            { once: true },
          );
          img.addEventListener(
            "error",
            () => {
              img.removeAttribute("data-loading");
              resolve();
            },
            { once: true },
          );

          img.fetchPriority = priority;
          img.loading = "eager";
          img.decoding = "async";
          img.removeAttribute("data-src");
          img.removeAttribute("data-srcset");
          if (srcset) img.srcset = srcset;
          img.src = src;
        }),
    );

    return Promise.race([
      Promise.all(loaded).then(() => undefined),
      new Promise((resolve) => setTimeout(resolve, 10000)),
    ]);
  }

  /**
   * Loads each submenu's images in the background once the page has loaded.
   * Skipped on slow or data-saving connections, where intent still loads them.
   */
  #queueIdleImageLoad() {
    const connection = navigator.connection;
    if (
      connection?.saveData ||
      /^(slow-2g|2g|3g)$/.test(connection?.effectiveType ?? "")
    ) {
      return;
    }

    const queue = [...this.querySelectorAll(".header-menu__submenu")];
    const idle = (callback) =>
      "requestIdleCallback" in window
        ? window.requestIdleCallback(callback, { timeout: 5000 })
        : setTimeout(callback, 200);

    const next = async () => {
      const submenu = queue.shift();
      if (!submenu || !this.isConnected) return;

      await this.#loadMenuImages(submenu, "low");
      idle(next);
    };

    const start = () => setTimeout(() => idle(next), 2000);

    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
  }

  /**
   * Presets the min-height of every submenu grid from its default content,
   * mirroring what happens when a submenu opens. Desktop only — the mobile
   * panels are full-height overlays whose grid uses `display: contents`.
   */
  #initSubmenuHeights() {
    if (!isFromBreakpoint("large")) return;

    for (const submenu of this.querySelectorAll(".header-menu__submenu")) {
      this.#showDefaultProducts(submenu);
    }
  }

  /**
   * Opens the submenu for the hovered link and closes the rest. Hovering a
   * collection item swaps the product grid shown in the remaining columns.
   * Hovering the submenu itself leaves the current state untouched so it stays
   * open while the pointer moves into it. Desktop only — touch devices use
   * {@link #onClick} to drill in.
   * @param {MouseEvent} event
   */
  #onMouseOver = (event) => {
    if (!isFromBreakpoint("large")) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const collectionItem = target.closest(
      ".header-menu__submenu-collection-item",
    );
    if (collectionItem && this.contains(collectionItem)) {
      this.#showCollectionProducts(collectionItem);
      this.#loadMenuImages(
        collectionItem.closest(".header-menu__submenu-collection") ??
          collectionItem,
        "high",
      );
      return;
    }

    // A plain link placed in a collection list has no products of its own, so
    // clear the product area and keep it blank while it is hovered.
    const collectionLink = target.closest(
      ".header-menu__submenu-collection-list .header-menu__submenu-link-item",
    );
    if (collectionLink && this.contains(collectionLink)) {
      this.#hideCollectionProducts(collectionLink);
      return;
    }

    const link = target.closest(".header-menu__link");
    if (!link || !this.contains(link)) return;

    const submenu = link
      .closest(".header-menu__item")
      ?.querySelector(".header-menu__submenu");

    for (const menu of this.querySelectorAll(".header-menu__submenu")) {
      menu.classList.toggle(classes.open, menu === submenu);
    }

    if (submenu) {
      this.#showDefaultProducts(submenu);
      this.#loadMenuImages(
        submenu.querySelector(
          `.header-menu__submenu-products.${classes.active}`,
        ) ?? submenu,
        "high",
      );
      this.#loadMenuImages(submenu, "low");
    }
  };

  /**
   * Reveals the product grid for a hovered collection item and hides the
   * others within the same submenu.
   * @param {Element} collectionItem
   */
  #showCollectionProducts(collectionItem) {
    const submenu = collectionItem.closest(".header-menu__submenu");
    const products = collectionItem
      .closest(".header-menu__submenu-collection")
      ?.querySelector(".header-menu__submenu-products");
    if (!submenu) return;

    // A collection item was hovered, so undo any blanking left by a plain link.
    for (const list of submenu.querySelectorAll(
      ".header-menu__submenu-collection-list",
    )) {
      list.classList.remove("products-hidden");
    }

    for (const panel of submenu.querySelectorAll(
      ".header-menu__submenu-products",
    )) {
      const active = panel === products;
      panel.classList.toggle(classes.active, active);
      this.#setProductsPanelEnabled(panel, active);
    }

    for (const item of submenu.querySelectorAll(
      ".header-menu__submenu-collection-item",
    )) {
      item.classList.toggle(classes.active, item === collectionItem);
    }

    this.#syncProductsHeight(submenu, products);
  }

  /**
   * Blanks the product area when a plain link inside a collection list is
   * hovered. The reserved grid height is left untouched so the mega menu keeps
   * its size instead of collapsing while the link is hovered.
   * @param {Element} linkItem
   */
  #hideCollectionProducts(linkItem) {
    const submenu = linkItem.closest(".header-menu__submenu");
    const list = linkItem.closest(".header-menu__submenu-collection-list");
    if (!submenu || !list) return;

    for (const panel of submenu.querySelectorAll(
      ".header-menu__submenu-products",
    )) {
      panel.classList.remove(classes.active);
      this.#setProductsPanelEnabled(panel, false);
    }

    for (const item of submenu.querySelectorAll(
      ".header-menu__submenu-collection-item",
    )) {
      item.classList.remove(classes.active);
    }

    list.classList.add("products-hidden");
  }

  /**
   * Inactive product grids stack over the visible one. Swiper fade slides use
   * `pointer-events: auto`, which would otherwise punch through parent
   * `pointer-events: none` and send clicks to the collection underneath.
   * @param {Element} panel
   * @param {boolean} enabled
   */
  #setProductsPanelEnabled(panel, enabled) {
    panel.toggleAttribute("inert", !enabled);
    panel.setAttribute("aria-hidden", enabled ? "false" : "true");
  }

  /**
   * Ensures the first collection's products are shown when a submenu opens so
   * the remaining columns are never empty.
   * @param {Element} submenu
   */
  #showDefaultProducts(submenu) {
    const active = submenu.querySelector(
      ".header-menu__submenu-products.is-active",
    );
    if (active) {
      this.#syncProductsHeight(submenu, active);
      return;
    }

    const firstItem = submenu.querySelector(
      ".header-menu__submenu-collection-item",
    );
    if (firstItem) this.#showCollectionProducts(firstItem);
  }

  /**
   * The product grid is absolutely positioned, so grow the submenu grid to fit
   * the currently visible panel.
   * @param {Element} submenu
   * @param {Element | null | undefined} products
   */
  #syncProductsHeight(submenu, products) {
    // The absolute product grid only exists on the desktop mega menu.
    if (!isFromBreakpoint("large")) return;

    const items = submenu.querySelector(".header-menu__submenu-items");
    if (!(items instanceof HTMLElement)) return;

    items.style.minHeight = products
      ? `${/** @type {HTMLElement} */ (products).scrollHeight}px`
      : "";
  }

  /**
   * Closes the hovered submenu when the pointer leaves the menu. Desktop only.
   */
  #onMouseLeave = () => {
    if (!isFromBreakpoint("large")) return;
    this.#closeAllSubmenus();
  };

  /**
   * Mobile drill-in: tapping a menu item with a submenu opens its panel; the
   * back button returns to the previous layer. Items without a submenu keep
   * their default link behaviour.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const back = target.closest(".header-menu__submenu-back");
    if (back && this.contains(back)) {
      event.preventDefault();
      // Close only the panel the back button belongs to, returning one layer.
      const panel = back.closest(
        ".header-menu__submenu-banner-panel, .header-menu__submenu",
      );
      if (panel) panel.classList.remove(classes.open);
      else this.#closeAllSubmenus();
      return;
    }

    if (!isUntilBreakpoint("large")) return;

    // A banner with links drills into a further panel instead of navigating.
    const banner = target.closest(".header-menu__submenu-banner");
    if (banner && this.contains(banner)) {
      const panel = banner.querySelector(".header-menu__submenu-banner-panel");
      // Clicks inside the open panel (e.g. its links) navigate as normal.
      if (panel && !panel.contains(target)) {
        const hasLinks = panel.querySelector(".header-menu__submenu-link-item");
        if (hasLinks) {
          event.preventDefault();
          // Reset the grid's scroll so the overlaying panel aligns to the top.
          const submenu = banner.closest(".header-menu__submenu");
          if (submenu) submenu.scrollTop = 0;
          panel.classList.add(classes.open);
        }
      }
      return;
    }

    const link = target.closest(".header-menu__link");
    if (!link || !this.contains(link)) return;

    const submenu = link
      .closest(".header-menu__item")
      ?.querySelector(".header-menu__submenu");

    // No submenu: let the link navigate as normal.
    if (!submenu) return;

    event.preventDefault();

    for (const menu of this.querySelectorAll(".header-menu__submenu")) {
      menu.classList.toggle(classes.open, menu === submenu);
    }
  };

  #closeAllSubmenus = () => {
    for (const menu of this.querySelectorAll(
      ".header-menu__submenu, .header-menu__submenu-banner-panel",
    )) {
      menu.classList.remove(classes.open);
    }

    // Desktop keeps each grid's reserved height (set on load from its content)
    // so the layout stays stable between opens. Only mobile clears it, to avoid
    // carrying a stale height across a breakpoint change.
    if (isFromBreakpoint("large")) return;

    for (const items of this.querySelectorAll(".header-menu__submenu-items")) {
      if (items instanceof HTMLElement) items.style.minHeight = "";
    }
  };

  /**
   * Opens the submenu belonging to the selected block (either the menu item
   * itself or a nested submenu block) so it is visible in the theme editor.
   * @param {CustomEvent} event
   */
  onBlockSelect(event) {
    const target = /** @type {Element} */ (event.target);
    const submenu = target
      ?.closest?.(".header-menu__item")
      ?.querySelector(".header-menu__submenu");

    for (const menu of this.querySelectorAll(".header-menu__submenu")) {
      menu.classList.toggle(classes.open, menu === submenu);
    }

    if (submenu) {
      const collectionItem =
        target?.closest?.(".header-menu__submenu-collection-item") ??
        target?.querySelector?.(".header-menu__submenu-collection-item");

      const collectionLink =
        target?.closest?.(
          ".header-menu__submenu-collection-list .header-menu__submenu-link-item",
        ) ??
        target
          ?.closest?.(".header-menu__submenu-collection-list")
          ?.querySelector?.(".header-menu__submenu-link-item");

      if (collectionItem) {
        this.#showCollectionProducts(collectionItem);
      } else if (collectionLink) {
        this.#hideCollectionProducts(collectionLink);
      } else {
        this.#showDefaultProducts(submenu);
      }
    }

    // Drill into a banner's links panel when the banner (or a nested link) is
    // selected, so it is visible in the editor.
    const bannerPanel =
      target?.closest?.(".header-menu__submenu-banner-panel") ??
      target
        ?.closest?.(".header-menu__submenu-banner")
        ?.querySelector(".header-menu__submenu-banner-panel");

    for (const panel of this.querySelectorAll(
      ".header-menu__submenu-banner-panel",
    )) {
      panel.classList.toggle(classes.open, panel === bannerPanel);
    }

    document.documentElement.classList.add("header-scrolled");
  }

  onBlockDeselect() {
    this.#closeAllSubmenus();
    document.documentElement.classList.remove("header-scrolled");
  }

  /**
   * Sets the default open states.
   */
  setDefaultState() {}
}

const HEADER_ANNOUNCEMENTS_DESKTOP = `(min-width: ${breakpoints.large}px)`;

class HeaderAnnouncements extends HTMLCustomElement {
  activeIndex = 0;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #autoplayInterval;

  /** @type {MediaQueryList | undefined} */
  #desktopMql;

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.#desktopMql = window.matchMedia(HEADER_ANNOUNCEMENTS_DESKTOP);
    this.#desktopMql.addEventListener(
      "change",
      this.#onDesktopBreakpointChange,
    );
    this.#syncLayoutForBreakpoint(true);

    this.on("mouseenter", this, () => {
      this.paused = true;
    });

    this.on("mouseleave", this, () => {
      this.paused = false;
    });
  }

  disconnectedCallback() {
    this.#clearAutoplay();
    this.#desktopMql?.removeEventListener(
      "change",
      this.#onDesktopBreakpointChange,
    );
    this.#desktopMql = undefined;
    super.disconnectedCallback();
  }

  #onDesktopBreakpointChange = () => {
    this.#syncLayoutForBreakpoint();
  };

  /**
   * Desktop shows every announcement in a row; mobile keeps the rotating carousel.
   * @param {boolean} [immediate]
   */
  #syncLayoutForBreakpoint(immediate = false) {
    if (this.#desktopMql?.matches) {
      this.#clearAutoplay();
      for (const child of this.children) {
        child.classList.add(classes.active);
      }
      return;
    }

    this.#startAutoplay();
    this.showIndex(this.activeIndex, immediate);
  }

  #clearAutoplay() {
    if (this.#autoplayInterval !== undefined) {
      clearInterval(this.#autoplayInterval);
      this.#autoplayInterval = undefined;
    }
  }

  #startAutoplay() {
    if (this.#autoplayInterval !== undefined) return;

    this.#autoplayInterval = setInterval(
      () => {
        if (!this.paused) {
          this.showIndex(
            this.activeIndex === this.children.length - 1
              ? 0
              : this.activeIndex + 1,
          );
        }
      },
      getAttributeValue(this, "autoplay-delay", 5000),
    );
  }

  /**
   * Shows the provided index item.
   * @param {number} index - The item to show.
   * @param {boolean} immediate - Disables any transitions.
   */
  showIndex(index, immediate = false) {
    for (const [elementIndex, element] of Array.from(this.children).entries()) {
      let transition;
      const isActive = elementIndex === index;

      if (immediate) {
        transition = element.style.transition;

        element.style.transition = "none";
        element.offsetHeight;
      }

      element.classList.toggle(classes.active, isActive);

      if (immediate) {
        requestAnimationFrame(() => {
          element.style.transition = transition;
        });
      }
    }

    this.activeIndex = index;
  }
}

if (!customElements.get("header-menu"))
  customElements.define("header-menu", HeaderMenu);
if (!customElements.get("site-header"))
  customElements.define("site-header", SiteHeader);
if (!customElements.get("header-toolbar"))
  customElements.define("header-toolbar", HeaderToolbar);
if (!customElements.get("site-header-logo"))
  customElements.define("site-header-logo", SiteHeaderLogo);
if (!customElements.get("header-announcements"))
  customElements.define("header-announcements", HeaderAnnouncements);
